import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneIncoming,
  PhoneOutgoing,
  Voicemail,
  PhoneMissed,
  Phone,
  UserPlus,
  Ban,
  ShieldCheck,
  PhoneOff,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { Select } from '@/components/select';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { IdentifyDialog } from '@/components/identify-dialog';
import { Menu, type MenuItem } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { cn } from '@/lib/cn';
import { formatPhone, formatDateTime, formatDuration } from '@/lib/format';
import {
  useCalls,
  useDeleteCall,
  useMarkCallRead,
  usePurgeCalls,
  useRestoreCall,
  recordingUrl,
} from '@/lib/calls';
import {
  KIND_LABELS,
  kindTone,
  useBlockContact,
  useUnblockContact,
  useIdentifyNumber,
} from '@/lib/contacts-directory';
import { useSoftphone } from '@/lib/softphone';
import type { CallData } from '@/types/api';

function callName(c: CallData): string {
  return c.contact_name ?? c.client_name ?? c.lead_name ?? formatPhone(c.party_number);
}

export function CallsIndex() {
  const [filter, setFilter] = useState<
    '' | 'voicemail' | 'known' | 'unknown' | 'blocked' | 'screened' | 'deleted'
  >('');
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useCalls({
    filter: filter || undefined,
    page,
  });
  const rows = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5">
      <PageHeader
        icon={Phone}
        title="Calls"
        subtitle="Every call to and from the business line, with voicemail and transcriptions."
        action={
          filter === 'deleted' && rows && rows.length > 0 ? (
            <PurgeCallsButton total={meta?.total ?? rows.length} />
          ) : null
        }
      />

      <Select
        value={filter}
        onChange={(e) => {
          setFilter(
            e.target.value as '' | 'voicemail' | 'known' | 'unknown' | 'blocked' | 'deleted',
          );
          setPage(1);
        }}
        className="max-w-[200px]"
      >
        <option value="">All calls</option>
        <option value="voicemail">Voicemail</option>
        <option value="known">Known</option>
        <option value="unknown">Unknown</option>
        <option value="blocked">Blocked</option>
        <option value="screened">Screened</option>
        <option value="deleted">Deleted</option>
      </Select>

      {isLoading ? (
        <SkeletonList rows={6} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={Phone}
          title={filter === 'deleted' ? 'Nothing deleted' : 'No calls yet'}
          description={
            filter === 'deleted'
              ? 'Calls you delete wait here until you empty them for good.'
              : 'Calls and voicemail on your business line will show up here.'
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {rows.map((call) => (
              <CallRow key={call.id} call={call} deleted={filter === 'deleted'} />
            ))}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Empties the deleted list. Scoped to the filters on screen, the same way the
 * form inbox purge is, and this one also asks Twilio to delete the voicemail
 * audio behind each row, which is the only thing that stops paying storage for
 * a recording nothing points at any more.
 */
function PurgeCallsButton({ total }: { total: number }) {
  const purge = usePurgeCalls();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <Button
      variant="danger"
      disabled={purge.isPending}
      onClick={async () => {
        const ok = await confirm({
          title: `Permanently delete ${total} call${total === 1 ? '' : 's'}?`,
          body: 'This cannot be undone. The records go, and Twilio is asked to delete any voicemail audio behind them.',
          confirmLabel: 'Delete for good',
          tone: 'danger',
        });
        if (!ok) return;
        const purged = await purge.mutateAsync({ filter: 'deleted' });
        toast.success(`${purged} call${purged === 1 ? '' : 's'} deleted for good`);
      }}
    >
      <Trash2 className="size-4" /> Empty deleted
    </Button>
  );
}

function CallIcon({ call }: { call: CallData }) {
  const cls = 'size-4';
  if (call.answered_by === 'screened') {
    return <PhoneOff className={cn(cls, 'text-muted-foreground')} />;
  }
  if (call.answered_by === 'voicemail') {
    return <Voicemail className={cn(cls, 'text-[var(--color-warning)]')} />;
  }
  if (call.direction === 'inbound') {
    return call.answered_by === 'browser' ? (
      <PhoneIncoming className={cn(cls, 'text-[var(--color-success)]')} />
    ) : (
      <PhoneMissed className={cn(cls, 'text-[var(--color-danger)]')} />
    );
  }
  return <PhoneOutgoing className={cn(cls, 'text-muted-foreground')} />;
}

function CallRow({ call, deleted }: { call: CallData; deleted: boolean }) {
  const sp = useSoftphone();
  const markRead = useMarkCallRead();
  const block = useBlockContact();
  const unblock = useUnblockContact();
  const identify = useIdentifyNumber();
  const remove = useDeleteCall();
  const restore = useRestoreCall();
  const confirm = useConfirm();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const name = callName(call);
  const hasVoicemail = call.has_recording;
  const blocking = block.isPending || unblock.isPending || identify.isPending;

  async function handleDelete() {
    const ok = await confirm({
      title: 'Delete this call?',
      body: hasVoicemail
        ? 'It moves to the deleted list, voicemail and all, where you can restore it or empty it for good.'
        : 'It moves to the deleted list, where you can restore it or empty it for good.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    await remove.mutateAsync(call.id);
    toast.success('Call deleted');
  }

  // Block and unblock move in here rather than sitting as buttons: the row
  // already carries Identify, call back and Listen, and a fourth control left
  // no room for Delete.
  const menuItems: MenuItem[] = deleted
    ? [
        {
          key: 'restore',
          label: 'Restore',
          icon: RotateCcw,
          onSelect: async () => {
            await restore.mutateAsync(call.id);
            toast.success('Call restored');
          },
        },
      ]
    : [
        ...(call.direction === 'inbound' && call.is_blocked
          ? [{ key: 'unblock', label: 'Unblock', icon: ShieldCheck, onSelect: handleUnblock, disabled: blocking }]
          : []),
        ...(call.direction === 'inbound' && !call.is_blocked && call.contact_id === null
          ? [{ key: 'block', label: 'Block', icon: Ban, onSelect: handleBlock, disabled: blocking }]
          : []),
        { key: 'delete', label: 'Delete', icon: Trash2, onSelect: handleDelete, danger: true },
      ];

  function toggle() {
    if (!hasVoicemail) return;
    const next = !open;
    setOpen(next);
    if (next && !call.is_read) markRead.mutate(call.id);
  }

  async function handleBlock() {
    try {
      // An identified caller blocks by contact; an unknown number blocks by
      // creating + flagging its contact via the identify endpoint.
      if (call.contact_id !== null) {
        await block.mutateAsync(call.contact_id);
      } else {
        await identify.mutateAsync({ number: call.party_number, action: 'block' });
      }
      toast.success('Number blocked. Their calls will be rejected.');
    } catch {
      toast.error('Could not block that number.');
    }
  }

  async function handleUnblock() {
    if (call.contact_id === null) return;
    try {
      await unblock.mutateAsync(call.contact_id);
      toast.success('Number unblocked.');
    } catch {
      toast.error('Could not unblock that number.');
    }
  }

  return (
    <DataListRow interactive={false} className="flex-col !items-stretch">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-surface-muted)]">
          <CallIcon call={call} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {call.contact_id !== null ? (
              <Link
                to={`/contacts/${call.contact_id}`}
                className={cn(
                  'truncate hover:text-primary hover:underline',
                  call.is_read ? 'font-medium' : 'font-semibold',
                )}
              >
                {name}
              </Link>
            ) : (
              <span className={cn('truncate', call.is_read ? 'font-medium' : 'font-semibold')}>
                {name}
              </span>
            )}
            {call.contact_kind ? (
              <Badge tone={kindTone(call.contact_kind)}>
                {KIND_LABELS[call.contact_kind] ?? call.contact_kind}
              </Badge>
            ) : null}
            {!call.is_read && hasVoicemail ? (
              <span className="size-2 shrink-0 rounded-full bg-[var(--color-primary)]" aria-label="Unheard" />
            ) : null}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {call.answered_by === 'screened'
              ? 'Screened'
              : call.answered_by === 'voicemail'
                ? 'Voicemail'
                : call.direction === 'inbound'
                  ? call.answered_by === 'browser'
                    ? 'Incoming'
                    : 'Missed'
                  : 'Outgoing'}
            {' · '}
            {formatPhone(call.party_number)}
            {call.recording_duration ? ` · ${formatDuration(call.recording_duration)}` : ''}
            {' · '}
            {formatDateTime(call.created_at)}
            {call.client_id ? (
              <>
                {' · '}
                <Link to={`/clients/${call.client_id}`} className="hover:text-foreground">
                  {call.client_name}
                </Link>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {call.contact_id === null ? (
            <Button size="sm" variant="secondary" onClick={() => setIdentifying(true)}>
              <UserPlus className="size-3.5" /> Identify
            </Button>
          ) : null}
          <button
            type="button"
            onClick={() => sp.makeCall(call.party_number)}
            className="inline-flex size-8 items-center justify-center rounded-full text-[var(--color-success)] hover:bg-[var(--color-success)]/10"
            title={`Call ${formatPhone(call.party_number)}`}
            aria-label="Call back"
          >
            <PhoneOutgoing className="size-4" />
          </button>
          {hasVoicemail ? (
            <button
              type="button"
              onClick={toggle}
              className="rounded-md px-2 py-1 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
            >
              {open ? 'Hide' : 'Listen'}
            </button>
          ) : null}
          <Menu items={menuItems} />
        </div>
      </div>

      {open && hasVoicemail ? (
        <div className="mt-3 space-y-2 pl-11">
          <audio controls preload="none" src={recordingUrl(call.id)} className="w-full max-w-md">
            Your browser does not support audio playback.
          </audio>
          {call.voicemail_transcription ? (
            <p className="max-w-2xl whitespace-pre-line rounded-lg bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-foreground)]">
              “{call.voicemail_transcription}”
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Transcription pending…</p>
          )}
          {call.client_request_id ? (
            <Link
              to="/requests"
              className="inline-block text-xs font-medium text-[var(--color-primary)] hover:underline"
            >
              View the request this opened →
            </Link>
          ) : null}
        </div>
      ) : null}

      <IdentifyDialog
        number={call.party_number}
        open={identifying}
        onClose={() => setIdentifying(false)}
      />
    </DataListRow>
  );
}
